import React from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faCartPlus,
  faEnvelope,
  faPenToSquare,
  faEllipsisV,
} from "@fortawesome/free-solid-svg-icons";
import { Link } from "react-router-dom";
import {
  FaUser,
  FaMapMarkerAlt,
  FaGlobe,
  FaPhoneAlt,
  FaBuilding,
} from "react-icons/fa";

const VendorProfileCard = (props) => {
  const details = props.Details || {};

  const isLeadDetailsPage = window.location.pathname.includes("lead-details");

  // FIX: the header fallback (shown before a name has loaded, or when the
  // record has no firstname) was hardcoded to "Lead Profile" regardless of
  // which page rendered this shared card — so Customer and Vendor pages
  // incorrectly showed "Lead Profile" too. This mirrors the existing
  // isLeadDetailsPage / edit-link pathname checks already used elsewhere
  // in this file, so the label matches whichever page is actually showing
  // the card.
  const getProfileLabel = () => {
    const path = window.location.pathname;
    if (path.includes("lead-details")) return "Lead Profile";
    if (path.includes("vendor-details")) return "Vendor Profile";
    if (path.includes("customer-details")) return "Customer Profile";
    return "Profile";
  };

  const getStatusBadge = (status) => {
    switch (status?.toLowerCase()) {
      case "close":
      case "closed":
        return "bg-danger-subtle text-danger border-danger-subtle";
      case "open":
      case "new":
        return "bg-success-subtle text-success border-success-subtle";
      case "inbound":
      case "outbound":
        return "bg-info-subtle text-info border-info-subtle";
      default:
        return "bg-primary-subtle text-primary border-primary-subtle";
    }
  };

  const formatStatus = (status) => {
    if (!status) return "—";
    return status
      .replace(/_/g, " ")
      .replace("TOCUSTOMER", "TO CUSTOMER")
      .toLowerCase()
      .replace(/\b\w/g, (l) => l.toUpperCase());
  };

  const truncateText = (text, maxLength = 20) => {
    if (!text) return "—";
    return text.length > maxLength ? text.substring(0, 8) + "..." : text;
  };

  const getEditLink = () => {
    const path = window.location.pathname;
    if (path.includes("lead-details")) {
      return `/edit-lead/${details.id}`;
    } else if (path.includes("vendor-details")) {
      return `/new-vendor/${details.id}`;
    } else {
      return `/edit-customer/${details.id}`;
    }
  };

  return (
    <div className="card border-0 shadow-sm rounded-3 mb-4 overflow-hidden bg-white">
      {/* Profile Header */}
      <div className="card-header bg-white py-3 px-3 border-bottom d-flex align-items-center justify-content-between">
        <div>
          <h5 className="card-title mb-1 fw-bold text-dark fs-6">
            {details.firstname ? `${details.firstname} ${details.lastname || ""}` : getProfileLabel()}
          </h5>
          {details.status && (
            <span
              className={`badge border px-2 py-1 text-uppercase fw-semibold ${getStatusBadge(details.status)}`}
              style={{ fontSize: "10px", borderRadius: "6px" }}
            >
              {details.status}
            </span>
          )}
        </div>

        <div className="dropdown">
          <button
            className="btn btn-outline-secondary btn-sm px-2.5 rounded-2 d-inline-flex align-items-center gap-1"
            type="button"
            id="dropdownMenuButton"
            data-bs-toggle="dropdown"
            aria-expanded="false"
            style={{ fontSize: "0.8rem" }}
          >
            <FontAwesomeIcon icon={faEllipsisV} /> Actions
          </button>
          <ul
            className="dropdown-menu dropdown-menu-end shadow-sm border p-1"
            aria-labelledby="dropdownMenuButton"
            style={{ borderRadius: "8px" }}
          >
            <li>
              <Link
                to={getEditLink()}
                className="dropdown-item rounded py-1.5 px-3 fs-7"
              >
                <FontAwesomeIcon icon={faPenToSquare} className="me-2 text-primary" /> Edit Details
              </Link>
            </li>
            <li>
              <a className="dropdown-item rounded py-1.5 px-3 fs-7" href={`mailto:${details.email || ""}`}>
                <FontAwesomeIcon icon={faEnvelope} className="me-2 text-danger" /> Send Mail
              </a>
            </li>
            {!isLeadDetailsPage && (
              <li>
                <Link to={`/product/${details.id}`} className="dropdown-item rounded py-1.5 px-3 fs-7">
                  <FontAwesomeIcon icon={faCartPlus} className="me-2 text-success" /> Add Product
                </Link>
              </li>
            )}
          </ul>
        </div>
      </div>

      <div className="card-body p-3">
        {/* Personal Details Section */}
        <div className="mb-3">
          <div className="d-flex align-items-center gap-2 mb-2">
            <span
              className="rounded-circle d-flex align-items-center justify-content-center p-1.5 bg-slate-100 text-secondary"
              style={{ width: "26px", height: "26px" }}
            >
              <FaUser size={13} />
            </span>
            <span className="fw-semibold text-dark fs-7 text-uppercase" style={{ letterSpacing: "0.5px" }}>Personal Details</span>
          </div>
          <div className="d-flex justify-content-between py-1 border-bottom border-light">
            <span className="text-muted small">Owner</span>
            <span className="fw-medium text-dark small text-end" title={details.owner}>
              {details.owner
                ? (details.owner.length > 20 ? details.owner.substring(0, 8) + "..." : details.owner)
                : <span style={{ color: "#bbb", fontStyle: "italic" }}>—</span>}
            </span>
          </div>
          <div className="d-flex justify-content-between py-1 border-bottom border-light">
            <span className="text-muted small">Status</span>
            <span className="fw-medium text-dark small">
              {details.status
                ? details.status.replace(/_/g, " ").replace("TOCUSTOMER", "TO CUSTOMER").toLowerCase().replace(/\b\w/g, l => l.toUpperCase())
                : <span style={{ color: "#bbb", fontStyle: "italic" }}>—</span>
              }
            </span>
          </div>
          <div className="d-flex align-items-center gap-2 mb-2 mt-2">
            <span className="fw-semibold text-dark fs-7 text-uppercase" style={{ letterSpacing: "0.5px" }}>Address</span>
          </div>

          <div className="ps-4">
            <div className="d-flex justify-content-between py-1 border-bottom border-light">
              <span className="text-muted small">Street:</span>
              <span className="fw-medium text-dark small text-end">{details.add1 || details.add2 || "—"}</span>
            </div>
            <div className="d-flex justify-content-between py-1 border-bottom border-light">
              <span className="text-muted small">City / State:</span>
              <span className="fw-medium text-dark small">
                {details.city || "—"}{details.state ? `, ${details.state}` : ""}
              </span>
            </div>
            <div className="d-flex justify-content-between py-1 border-bottom border-light">
              <span className="text-muted small">Country / Pincode:</span>
              <span className="fw-medium text-dark small">
                {details.country || "—"}{details.pincode ? ` - ${details.pincode}` : ""}
              </span>
            </div>
          </div>
        </div>

        {/* Company & Web Section */}
        <div className="mb-3">
          <div className="d-flex align-items-center gap-2 mb-2">
            <span
              className="rounded-circle d-flex align-items-center justify-content-center p-1.5 bg-slate-100 text-secondary"
              style={{ width: "26px", height: "26px" }}
            >
              <FaBuilding size={13} />
            </span>
            <span className="fw-semibold text-dark fs-7 text-uppercase" style={{ letterSpacing: "0.5px" }}>
              Company & Web
            </span>
          </div>
          <div className="ps-4">
            <div className="d-flex justify-content-between py-1 border-bottom border-light">
              <span className="text-muted small">Company:</span>
              <span className="fw-medium text-dark small">{details.companyname || "—"}</span>
            </div>
            <div className="d-flex justify-content-between py-1 border-bottom border-light">
              <span className="text-muted small">Website:</span>
              <span className="fw-medium text-primary small text-truncate ms-2" style={{ maxWidth: "180px" }}>
                {details.website ? (
                  <a
                    href={details.website.startsWith("http") ? details.website : `https://${details.website}`}
                    target="_blank"
                    rel="noreferrer"
                    className="text-primary text-decoration-none"
                  >
                    {details.website}
                  </a>
                ) : (
                  "—"
                )}
              </span>
            </div>
          </div>
        </div>

        {/* Contact Phone Section */}
        <div>
          <div className="d-flex align-items-center gap-2 mb-2">
            <span
              className="rounded-circle d-flex align-items-center justify-content-center p-1.5 bg-slate-100 text-secondary"
              style={{ width: "26px", height: "26px" }}
            >
              <FaPhoneAlt size={13} />
            </span>
            <span className="fw-semibold text-dark fs-7 text-uppercase" style={{ letterSpacing: "0.5px" }}>
              Contact Phone
            </span>
          </div>
          <div className="ps-4">
            <div className="d-flex justify-content-between py-1 border-bottom border-light">
              <span className="text-muted small">Mobile:</span>
              <span className="fw-medium text-dark small">{details.mobile || "—"}</span>
            </div>
            <div className="d-flex justify-content-between py-1">
              <span className="text-muted small">Alternate:</span>
              <span className="fw-medium text-dark small">{details.altphone || "—"}</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default VendorProfileCard;